/**
 * Summary Cards Component
 * Displays four summary cards: Traffic, Keywords, SERP Features, Competitors
 */

import React, { useState } from 'react';
import CompetitorAnalysisModal from './CompetitorAnalysisModal';

interface SummaryCardsProps {
    userId: string;
    data: {
        organic_traffic?: {
            total_clicks: number;
            total_impressions: number;
            data_available: boolean;
        };
        keywords_performance?: {
            avg_position: number;
            total_keywords: number;
            tracked_keywords_count: number;
            data_available: boolean;
        };
        serp_features?: {
            features_count: number;
            features_present: string[];
            domain_in_features: number;
            target_feature_presence: Record<string, number>;
            data_available: boolean;
            message?: string;
        };
        competitor_rank?: {
            your_rank: number | null;
            total_competitors: number;
            your_visibility_score: number;
            empty_state: boolean;
            message?: string;
        };
    } | null;
    hasError?: boolean;
}

const SummaryCards: React.FC<SummaryCardsProps> = ({ userId, data, hasError = false }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    if (!data) {
        return (
            <div className="summary-cards-grid">
                {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="summary-card skeleton">
                        <div className="skeleton-title"></div>
                        <div className="skeleton-value"></div>
                    </div>
                ))}
            </div>
        );
    }

    const traffic = data.organic_traffic;
    const keywords = data.keywords_performance;
    const serp = data.serp_features;

    const formatNumber = (num: number) => {
        if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
        if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
        return num.toString();
    };

    return (
        <>
            <div className="summary-cards-grid">
                {/* Organic Traffic Card */}
                <div className="summary-card traffic-card">
                    <div className="card-header">
                        <span className="card-icon">📊</span>
                        <h3>Organic Traffic</h3>
                    </div>
                    {hasError ? (
                        <div className="card-empty">
                            <span className="empty-icon text-red-500">!</span>
                            <span className="empty-text text-red-500">Failed to load data from server</span>
                        </div>
                    ) : traffic?.data_available ? (
                        <div className="card-content">
                            <div className="metric-row">
                                <span className="metric-label">Clicks</span>
                                <span className="metric-value">{formatNumber(traffic.total_clicks)}</span>
                            </div>
                            <div className="metric-row">
                                <span className="metric-label">Impressions</span>
                                <span className="metric-value">{formatNumber(traffic.total_impressions)}</span>
                            </div>
                        </div>
                    ) : (
                        <div className="card-empty">
                            <span className="empty-icon">—</span>
                            <span className="empty-text">Connect GSC to view data</span>
                        </div>
                    )}
                </div>

                {/* Keywords Performance Card */}
                <div className="summary-card keywords-card">
                    <div className="card-header">
                        <span className="card-icon">🔑</span>
                        <h3>Keywords</h3>
                    </div>
                    {hasError ? (
                        <div className="card-empty">
                            <span className="empty-icon text-red-500">!</span>
                            <span className="empty-text text-red-500">Failed to load data from server</span>
                        </div>
                    ) : keywords?.data_available ? (
                        <div className="card-content">
                            <div className="metric-row">
                                <span className="metric-label">Avg. Position</span>
                                {/* Show "--" when no keywords are actively ranking */}
                                <span className="metric-value">
                                    {keywords.avg_position > 0 ? keywords.avg_position.toFixed(1) : '--'}
                                </span>
                            </div>
                            <div className="metric-row">
                                <span className="metric-label">Ranking / Tracked</span>
                                {/* total_keywords = ranked count, tracked_keywords_count = universe size */}
                                <span className="metric-value">{keywords.total_keywords} <span style={{ fontSize: '0.6em', color: '#888' }}>/ {keywords.tracked_keywords_count}</span></span>
                            </div>
                        </div>
                    ) : (
                        <div className="card-empty">
                            <span className="empty-icon">—</span>
                            <span className="empty-text">Connect GSC to view data</span>
                        </div>
                    )}
                </div>

                {/* SERP Features Card */}
                <div className="summary-card serp-card">
                    <div className="card-header">
                        <span className="card-icon">⭐</span>
                        <h3>SERP Features</h3>
                    </div>
                    {hasError ? (
                        <div className="card-empty">
                            <span className="empty-icon text-red-500">!</span>
                            <span className="empty-text text-red-500">Failed to load data from server</span>
                        </div>
                    ) : (() => {
                        const CARD_FEATURES = [
                            { key: 'featured_snippet', name: 'Featured Snippet' },
                            { key: 'knowledge_graph',  name: 'Knowledge Panel' },
                            { key: 'local_pack',       name: 'Local Pack' },
                            { key: 'image_pack',       name: 'Image Pack' },
                            { key: 'video_results',    name: 'Video Carousel' },
                            { key: 'people_also_ask',  name: 'People Also Ask' },
                        ];
                        const brandPresence: Record<string, number> = serp?.target_feature_presence || {};
                        const activeCount = CARD_FEATURES.filter(f => f.key in brandPresence).length;
                        if (serp?.data_available) {
                            return (
                                <div className="card-content">
                                    <div className="serp-counts">
                                        <span className="serp-count active">{activeCount} Active</span>
                                        <span className="serp-count missing">{6 - activeCount} Missing</span>
                                        <span className="serp-count total">6 Total</span>
                                    </div>
                                    <div className="serp-feature-list">
                                        {CARD_FEATURES.map(f => {
                                            const present = f.key in brandPresence;
                                            return (
                                                <div key={f.key} className="serp-feature-row">
                                                    <span className="serp-feature-name">{f.name}</span>
                                                    <span className={present ? 'serp-check present' : 'serp-check missing'}>
                                                        {present ? '✓' : '✗'}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        }
                        return (
                            <div className="card-empty">
                                <span className="empty-icon">—</span>
                                <span className="empty-text">{serp?.message || 'SERP data unavailable'}</span>
                            </div>
                        );
                    })()}
                </div>

                {/* Competitor Rank Card */}
                <div
                    className="summary-card competitor-card clickable"
                    onClick={() => setIsModalOpen(true)}
                    style={{ cursor: 'pointer' }}
                >
                    <div className="card-header">
                        <span className="card-icon">🏆</span>
                        <h3>Competitive Position</h3>
                    </div>
                    <div className="card-content">
                        <div className="competitor-preview">
                            <p className="preview-text">Click to view detailed competitor analysis</p>
                            <p className="preview-subtext">Perceived vs Actual Competitors</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Competitor Analysis Modal */}
            <CompetitorAnalysisModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                userId={userId}
            />
        </>
    );
};

export default SummaryCards;
